import { IS_LOCAL } from "@/env";
import { useEffect } from "react";

import { captureEvent, pushToDataLayer } from "../_analytics/analytics";
import { EventName, GoogleTagManagerOnlyEventName } from "@mw/shared/analyticsEventNames";
import { logger } from "@utilities/logger";
import { isGPCEnabled } from "./gpc";
import { cancelIdleCallbackWithFallback, requestIdleCallbackWithFallback } from "./requestIdle";

// Should ALWAYS be false in prod
const LOCAL_TESTING_FLAG_INDICATE_EU = false;

export type CookiebotConsent = {
  marketing: boolean;
  necessary: boolean;
  preferences: boolean;
  statistics: boolean;
};

export type CookieBot = {
  consent: CookiebotConsent;
  hasResponse: boolean;
};

declare global {
  interface Window {
    Cookiebot: {
      consent: CookiebotConsent;
      hasResponse: boolean;
      renew: () => void;
    };
  }
}

export const fetchIsUserInEU = () => {
  if (IS_LOCAL && LOCAL_TESTING_FLAG_INDICATE_EU) {
    return true;
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone.includes("Europe");
  } catch (e) {
    logger.error(e instanceof Error ? e : null, "Failed to resolve EU timezone");
    return false;
  }
};

export const captureUserConsent = () => {
  if (typeof window === "undefined") {
    return;
  }

  let consentHandled = false;

  const handleConsentGiven = () => {
    if (consentHandled) return;
    window.removeEventListener("CookiebotOnConsentReady", handleConsentGiven);
    consentHandled = true;

    const consent = {
      statistics: window.Cookiebot.consent.statistics,
      marketing: window.Cookiebot.consent.marketing,
      preferences: window.Cookiebot.consent.preferences,
      necessary: window.Cookiebot.consent.necessary,
    };

    captureEvent(EventName.cookiebot_consent_recorded, {
      consent,
      gpcEnabled: isGPCEnabled(),
    });
  };

  // Check if Cookiebot already has consent
  if (window.Cookiebot?.hasResponse) {
    handleConsentGiven();
  }

  // Always set up listener for future consent events
  window.addEventListener("CookiebotOnConsentReady", handleConsentGiven);
};

// provides a trigger in GTM for the fb pixel that requires both consent and user attributes to be set (if logged in)
export const useSendPixelReadyAfterConsent = () => {
  useEffect(() => {
    let idleCallbackId: number | null = null;
    let consentHandled = false;

    const handleConsentReady = () => {
      if (consentHandled) return;
      window.removeEventListener("CookiebotOnConsentReady", handleConsentReady);
      consentHandled = true;

      idleCallbackId = requestIdleCallbackWithFallback(
        () => {
          pushToDataLayer(GoogleTagManagerOnlyEventName.ready_for_pixels);
        },
        { timeout: 2000 },
      );
    };

    // Check if Cookiebot already has consent
    if (window.Cookiebot?.hasResponse) {
      handleConsentReady();
    }

    // Always set up listener for future consent events
    window.addEventListener("CookiebotOnConsentReady", handleConsentReady);

    return () => {
      window.removeEventListener("CookiebotOnConsentReady", handleConsentReady);
      if (idleCallbackId !== null) {
        cancelIdleCallbackWithFallback(idleCallbackId);
      }
    };
  }, []);
};

// Name of the CustomEvent emitted by the Cookiebot banner
const COOKIEBOT_EVENT_NAME = "elevenlabs_cookiebot_event" as const;
// Map Cookiebot event names to analytics event names
const COOKIEBOT_EVENT_MAP = {
  audioPlayed: EventName.cookie_banner_audio_played,
  audioPaused: EventName.cookie_banner_audio_paused,
  decline: EventName.cookie_banner_decline,
  acceptAll: EventName.cookie_banner_accept_all,
  acceptSelection: EventName.cookie_banner_accept_selection,
} as const;
type CookiebotEvent = keyof typeof COOKIEBOT_EVENT_MAP;

export const useCookiebotEvents = () => {
  // Forward Cookiebot events to analytics. All events are sent as
  // `elevenlabs_cookiebot_event` with `type` and `properties` in the detail.
  useEffect(() => {
    function isCookiebotEvent(
      event: Event,
    ): event is CustomEvent<{ type: CookiebotEvent; properties: Record<string, unknown> }> {
      return (
        "detail" in event &&
        typeof event.detail === "object" &&
        event.detail !== null &&
        "type" in event.detail &&
        typeof event.detail.type === "string" &&
        event.detail.type in COOKIEBOT_EVENT_MAP &&
        "properties" in event.detail &&
        typeof event.detail.properties === "object" &&
        event.detail.properties !== null
      );
    }

    function handleCookiebotEvent(event: Event) {
      if (!isCookiebotEvent(event)) return;
      captureEvent(COOKIEBOT_EVENT_MAP[event.detail.type], event.detail.properties);
    }

    window.addEventListener(COOKIEBOT_EVENT_NAME, handleCookiebotEvent);

    return () => {
      window.removeEventListener(COOKIEBOT_EVENT_NAME, handleCookiebotEvent);
    };
  }, []);
};
